'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  X,
  Volume2,
  VolumeX,
  Minimize2,
  Maximize2,
  Shuffle,
  Gauge,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Slider } from '@/shared/components/ui/slider';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import type { PlayableItem } from '../types/player';

export type { PlayableItem } from '../types/player';

interface PlayQueueProps {
  items: PlayableItem[];
  onClose: () => void;
}

export function PlayQueue({ items, onClose }: PlayQueueProps) {
  const t = useTranslations('stories.playQueue');
  const [queue, setQueue] = useState<PlayableItem[]>(items);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('playQueue.playbackSpeed');
      return saved ? parseFloat(saved) : 1.0;
    }
    return 1.0;
  });
  const [isShuffled, setIsShuffled] = useState(false);
  const [originalQueue, setOriginalQueue] = useState<PlayableItem[]>([]);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);

  const currentItem = queue[currentIndex];

  // Create persistent Audio() instance ONCE — survives track changes.
  // AudioContext + createMediaElementSource are bound to this element for its entire lifetime.
  useEffect(() => {
    const audio = new Audio();
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    const connectWebAudio = () => {
      if (audioCtxRef.current) return;
      try {
        const ctx = new AudioContext();
        const source = ctx.createMediaElementSource(audio);
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 20000;
        filter.Q.value = 0.7;
        source.connect(filter);
        filter.connect(ctx.destination);
        audioCtxRef.current = ctx;
        filterRef.current = filter;
      } catch {
        // Fallback: AudioContext not available — play without filter
      }
    };

    audio.addEventListener('canplay', connectWebAudio, { once: true });

    return () => {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio.removeEventListener('canplay', connectWebAudio);
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close();
      }
      audioCtxRef.current = null;
      filterRef.current = null;
      audioRef.current = null;
    };
  }, []);

  // Handle next track with useCallback to avoid recreating function
  const handleNext = useCallback(() => {
    if (currentIndex < queue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      // Keep playing state true for auto-play next track
    } else {
      // End of queue
      setIsPlaying(false);
      setCurrentIndex(0);
    }
  }, [currentIndex, queue.length]);

  // Audio event handlers - attach once and use callbacks
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      // Auto-advance to next track
      if (currentIndex < queue.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setIsPlaying(false);
        setCurrentIndex(0);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentIndex, queue.length]);

  // Load current item when index changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentItem?.audioUrl) return;

    // Reset state for new track
    setCurrentTime(0);
    setDuration(0);

    // Load new audio source
    audio.src = mediaSrc(currentItem.audioUrl);
    audio.load();
  }, [currentIndex, currentItem]);

  // Handle play/pause state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentItem?.audioUrl) return;

    if (isPlaying) {
      const startPlayback = async () => {
        try {
          // Resume AudioContext on user gesture (browsers require it)
          if (audioCtxRef.current?.state === 'suspended') {
            await audioCtxRef.current.resume();
          }
          await audio.play();
        } catch (error) {
          console.error('Play failed:', error);
          setIsPlaying(false);
        }
      };
      startPlayback();
    } else {
      audio.pause();
    }
  }, [isPlaying, currentItem]);

  // Auto-play on mount
  useEffect(() => {
    setIsPlaying(true);
  }, []);

  // Apply playback speed and adjust low-pass filter to reduce artifacts
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackSpeed;
      // Cross-browser preservesPitch
      (audio as any).preservesPitch = true;
      (audio as any).mozPreservesPitch = true;
      (audio as any).webkitPreservesPitch = true;
    }
    // Adjust low-pass filter cutoff based on speed
    if (filterRef.current) {
      const cutoff = Math.min(20000, 20000 * playbackSpeed);
      filterRef.current.frequency.value = cutoff;
    }
  }, [playbackSpeed]);

  // Save playback speed to localStorage + notify other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('playQueue.playbackSpeed', playbackSpeed.toString());
    }
  }, [playbackSpeed]);

  // Sync playback speed from other components (same page + cross-tab)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'playQueue.playbackSpeed' && e.newValue) {
        setPlaybackSpeed(parseFloat(e.newValue));
      }
    };
    const handleSpeedSync = (e: Event) => {
      setPlaybackSpeed((e as CustomEvent<number>).detail);
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('playbackSpeedChange', handleSpeedSync);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('playbackSpeedChange', handleSpeedSync);
    };
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handlePrevious = useCallback(() => {
    if (currentTime > 3) {
      // If more than 3 seconds played, restart current track
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    } else if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentTime, currentIndex]);

  const handleSeek = useCallback((value: number[]) => {
    const time = value[0];
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const handleVolumeChange = useCallback((value: number[]) => {
    const vol = value[0];
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
    setIsMuted(vol === 0);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
      } else {
        audioRef.current.volume = 0;
      }
    }
  }, [isMuted, volume]);

  const handleSelectTrack = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Playback speed handler
  const handleSpeedChange = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
    window.dispatchEvent(new CustomEvent('playbackSpeedChange', { detail: speed }));
  }, []);

  // Shuffle handler
  const handleShuffle = useCallback(() => {
    if (!isShuffled) {
      // Save original order
      setOriginalQueue([...queue]);

      // Fisher-Yates shuffle
      const shuffled = [...queue];
      const currentItem = shuffled[currentIndex];

      // Remove current item
      shuffled.splice(currentIndex, 1);

      // Shuffle remaining items
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      // Put current item back at the beginning
      shuffled.unshift(currentItem);
      setQueue(shuffled);
      setCurrentIndex(0);
      setIsShuffled(true);
    } else {
      // Restore original order
      const currentItem = queue[currentIndex];
      const originalIndex = originalQueue.findIndex(item => item.id === currentItem.id);
      setQueue(originalQueue);
      setCurrentIndex(originalIndex >= 0 ? originalIndex : 0);
      setIsShuffled(false);
    }
  }, [isShuffled, queue, currentIndex, originalQueue]);

  // Remove from queue handler
  const handleRemoveFromQueue = useCallback((itemId: string) => {
    // Can't remove if only 1 item left
    if (queue.length <= 1) return;

    const itemIndex = queue.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return;

    const newQueue = queue.filter(item => item.id !== itemId);

    if (currentIndex === itemIndex) {
      // Removing current track, play next or previous
      const newIndex = Math.min(currentIndex, newQueue.length - 1);
      setCurrentIndex(newIndex);

      // If we're at the end and removing last item, pause
      if (newIndex === newQueue.length - 1 && currentIndex === queue.length - 1) {
        setIsPlaying(false);
      }
    } else if (itemIndex < currentIndex) {
      // Removing item before current, adjust index
      setCurrentIndex(currentIndex - 1);
    }

    setQueue(newQueue);

    // Update original queue if shuffled
    if (isShuffled) {
      setOriginalQueue(originalQueue.filter(item => item.id !== itemId));
    }
  }, [queue, currentIndex, isShuffled, originalQueue]);

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (queue.length === 0) return null;

  return (
    <div
      data-testid="play-queue"
      className={`fixed bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg transition-all duration-300 z-50 ${
        isMinimized ? 'h-16' : 'h-auto'
      }`}
    >
      {/* Minimized view */}
      {isMinimized && (
        <div className="h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Button
              onClick={handlePlayPause}
              className="p-2 min-touch rounded-full shrink-0"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </Button>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium text-foreground truncate">{currentItem.title}</span>
              <span className="text-xs text-muted-foreground">
                {currentIndex + 1} / {queue.length}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMinimized(false)}
              className="min-touch text-muted-foreground hover:text-foreground"
              title={t('expand')}
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="min-touch text-muted-foreground hover:text-foreground"
              title={t('close')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Full view */}
      {!isMinimized && (
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('title')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('playing', { current: currentIndex + 1, total: queue.length })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMinimized(true)}
                className="min-touch text-muted-foreground hover:text-foreground"
                title={t('minimize')}
              >
                <Minimize2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="min-touch text-muted-foreground hover:text-foreground"
                title={t('close')}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Current item info */}
          <div className="bg-muted rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-1">{currentItem.title}</h4>
            {currentItem.subtitle && (
              <p className="text-sm text-muted-foreground">{currentItem.subtitle}</p>
            )}
          </div>

          {/* Progress bar */}
          <div className="space-y-2">
            <Slider
              min={0}
              max={duration || 1}
              step={0.1}
              value={[currentTime]}
              onValueChange={handleSeek}
              className="w-full cursor-pointer"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            {/* Playback controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShuffle}
                className={`min-touch rounded-full ${
                  isShuffled
                    ? 'text-primary bg-primary/10 hover:bg-primary/20'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title={t('shuffle')}
              >
                <Shuffle className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrevious}
                disabled={currentIndex === 0 && currentTime < 3}
                className="min-touch rounded-full text-muted-foreground hover:text-foreground"
                title={t('previous')}
              >
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button
                onClick={handlePlayPause}
                className="p-3 min-h-[52px] min-w-[52px] rounded-full"
                title={isPlaying ? t('pause') : t('play')}
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                disabled={currentIndex === queue.length - 1}
                className="min-touch rounded-full text-muted-foreground hover:text-foreground"
                title={t('next')}
              >
                <SkipForward className="w-5 h-5" />
              </Button>
            </div>

            {/* Volume and speed controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="min-touch text-muted-foreground hover:text-foreground"
              >
                {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[isMuted ? 0 : volume]}
                onValueChange={handleVolumeChange}
                className="w-24 cursor-pointer"
              />
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  className="min-touch text-muted-foreground hover:text-foreground"
                  title={t('speed')}
                >
                  <Gauge className="w-5 h-5" />
                </Button>
                {showSpeedMenu && (
                  <div className="absolute bottom-full right-0 mb-2 bg-popover border border-border rounded-lg shadow-lg p-2 min-w-[100px]">
                    {[0.5, 1.0, 1.5, 2.0].map((speed) => (
                      <Button
                        key={speed}
                        variant="ghost"
                        onClick={() => handleSpeedChange(speed)}
                        className={`w-full justify-start text-sm ${
                          playbackSpeed === speed
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground'
                        }`}
                      >
                        {speed}x
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Queue list */}
          <div className="max-h-40 overflow-y-auto border-t border-border pt-3">
            <div className="space-y-1">
              {queue.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-1 group ${
                    index === currentIndex
                      ? 'bg-primary/10 rounded-md'
                      : ''
                  }`}
                >
                  <Button
                    variant="ghost"
                    onClick={() => handleSelectTrack(index)}
                    className={`flex-1 justify-start px-3 py-2 h-auto ${
                      index === currentIndex
                        ? 'text-primary font-medium hover:bg-primary/15'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-xs w-6 text-center">{index + 1}</span>
                      <span className="text-sm truncate flex-1">{item.title}</span>
                    </div>
                  </Button>
                  {queue.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFromQueue(item.id);
                      }}
                      className="min-touch shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      title={t('remove')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
